import { lazy, Suspense, useId, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { Link, useNavigate } from '@tanstack/react-router';
import { cn } from '@worldcoo/ui/lib/utils';
import { NativeSelect, NativeSelectOption } from '@worldcoo/ui/components/native-select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@worldcoo/ui/components/tabs';
import { Skeleton } from '@worldcoo/ui/components/skeleton';
import { DARK_FORM_FIELDS, DiamondGrid, diamondGlows, PillLink, pillSubmit } from '../primitives';
import { BuildingIcon, HeartIcon, MicrophoneIcon, PeopleIcon } from './icons';
import { Expand } from '../expand';
// Shared with the footer and the FAQ (TEC-3821).
import { DONOR_APP_URL, NGO_APPLICATION_FORM_URL } from '../external-links';
import { DEFAULT_NGO_TAB, type Audience, type NgoTab } from './audiences';

// Lazy: zod, the form runtime and the terms dialog wait until the visitor says who they are.
const ContactForm = lazy(() => import('@/routes/-components/contact-form').then((m) => ({ default: m.ContactForm })));

type SubmitCta = { label: string; className: string; adornment: ReactNode };

/** The hand-off's larger metrics, applied from outside so the shared form serving Solutions is untouched. */
const CONTACT_FORM_METRICS = [
	'[&_form]:gap-4',
	'[&_input]:h-12 [&_input]:px-4 [&_input]:text-base',
	'[&_textarea]:px-4 [&_textarea]:py-3 [&_textarea]:text-base',
	'[&_button[role=combobox]]:h-12 [&_button[role=combobox]]:px-4 [&_button[role=combobox]]:text-base',
	'[&_select]:h-12 [&_select]:pr-10 [&_select]:pl-4 [&_select]:text-base',
	'[&_[data-slot=native-select-icon]]:right-4',
].join(' ');

/** Sized to the shortest form, so picking an audience doesn't make the card jump. */
function FormSkeleton() {
	return (
		<div className="flex flex-col gap-4">
			{[0, 1].map((i) => (
				<div key={i} className="flex flex-col gap-1">
					<Skeleton className="h-5 w-16 bg-white/10" />
					<Skeleton className="h-12 w-full bg-white/10" />
				</div>
			))}
			<div className="flex flex-col gap-1">
				<Skeleton className="h-5 w-32 bg-white/10" />
				<Skeleton className="h-24 w-full bg-white/10" />
			</div>
			<Skeleton className="h-5 w-64 bg-white/10" />
			<Skeleton className="h-11 w-40 rounded-full bg-white/10" />
		</div>
	);
}

function SentMessage() {
	const { t } = useLingui();
	return (
		<div className="relative flex flex-1 flex-col justify-center gap-6 py-16 lg:max-w-[440px]">
			<h2 className="text-[32px] leading-[1.3] font-medium tracking-[-0.02em]">{t`Message sent successfully!`}</h2>
			<p className="leading-[1.5] text-white/70">
				{t`Thank you for getting in touch. We have received your message and will reply as soon as possible.`}
			</p>
		</div>
	);
}

/**
 * The picker NAVIGATES, so each audience is linkable and the back button works. `sent` is the page's,
 * since the left column's illustration animates on send.
 */
export function ContactPanel({
	audience,
	tab,
	sent,
	onSent,
}: {
	audience: Audience | null;
	/** Ignored by every audience but NGOs. */
	tab: NgoTab;
	sent: boolean;
	onSent: () => void;
}) {
	const { t } = useLingui();
	const navigate = useNavigate();
	const promptId = useId();

	const audiences: { value: Audience; label: string; icon: ComponentType<SVGProps<SVGSVGElement>> }[] = [
		{ value: 'company', label: t`Company`, icon: BuildingIcon },
		{ value: 'ngo', label: t`NGO / Social entity`, icon: HeartIcon },
		{ value: 'donor', label: t`Donor`, icon: PeopleIcon },
		{ value: 'press', label: t`Press and communication`, icon: MicrophoneIcon },
	];
	const selected = audiences.find(({ value }) => value === audience);
	const SelectedIcon = selected?.icon;

	// 48px/16px against the site's 44px/14px, the step `CONTACT_FORM_METRICS` applies to the fields.
	const submit: SubmitCta = pillSubmit(t`Send message`, { className: 'h-12 gap-2.5 pr-3 pl-5 text-base' });

	return (
		// The right-hand column, so last in the `Expand` sweep.
		<Expand index={1.5} asChild>
			<section className="bg-ink relative flex flex-col overflow-hidden rounded-3xl px-6 pt-8 pb-8 text-white md:px-12 md:pt-10 md:pb-12 lg:min-h-[801px]">
				{/* Measured off the hand-off's own ellipse. */}
				<DiamondGrid
					color="#fff"
					opacity={0.4}
					size={4.39}
					gap={27.59}
					masks={[diamondGlows({ x: '81.5%', y: '86.7%' })]}
				/>

				{sent ? (
					<SentMessage />
				) : (
					<>
						<h1 className="relative mb-6 text-[32px] leading-[1.3] font-medium tracking-[-0.02em]">
							<Trans>
								Contact <span className="text-olive">us</span>
							</Trans>
						</h1>

						<div className="relative flex flex-col gap-4">
							<p id={promptId} className="text-sm leading-[1.5] font-medium tracking-[-0.02em]">
								{t`Hi! I am…`}
							</p>

							{audience === null ? (
								<div role="group" aria-labelledby={promptId} className="flex flex-col gap-4">
									{audiences.map(({ value, label, icon: Icon }) => (
										<Link
											key={value}
											to="/www/contact/$audience"
											params={{ audience: value }}
											className="flex h-20 items-center justify-center gap-2 rounded-full border border-white px-4 py-3 font-medium transition-colors hover:bg-white/10"
										>
											<Icon className="size-4 shrink-0 opacity-50" />
											{label}
										</Link>
									))}
								</div>
							) : (
								/* Native, so the open list is the platform's; the icon laid over it must not swallow the click. */
								<div className="relative">
									{SelectedIcon && (
										<SelectedIcon className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 opacity-50" />
									)}
									<NativeSelect
										aria-labelledby={promptId}
										value={audience}
										// Looked up, not cast, so a non-audience string never becomes a route param.
										onChange={(event) => {
											const picked = audiences.find(({ value }) => value === event.target.value);
											if (picked) navigate({ to: '/www/contact/$audience', params: { audience: picked.value } });
										}}
										className={cn(
											'w-full',
											'[&_select]:h-12 [&_select]:rounded-lg [&_select]:border-white/10 [&_select]:bg-white/5',
											'[&_select]:pr-10 [&_select]:pl-[2.375rem] [&_select]:text-base [&_select]:text-white',
											'[&_option]:text-ink',
											'[&_[data-slot=native-select-icon]]:right-4 [&_[data-slot=native-select-icon]]:text-white/50',
										)}
									>
										{audiences.map(({ value, label }) => (
											<NativeSelectOption key={value} value={value}>
												{label}
											</NativeSelectOption>
										))}
									</NativeSelect>
								</div>
							)}
						</div>

						{/* NO Suspense here: opening the enquiry tab would swap the control just used for the skeleton. */}
						<div className={cn('relative mt-4', DARK_FORM_FIELDS, CONTACT_FORM_METRICS)}>
							{audience === 'ngo' ? (
								<NgoBody tab={tab} submit={submit} onSuccess={onSent} />
							) : (
								audience !== null && (
									<>
										{audience === 'donor' && (
											<div className="mb-6 flex flex-col gap-2">
												<h2 className="leading-[1.4] font-medium">
													{t`Do you have questions about how to deduct your donation?`}
												</h2>
												<p className="max-w-[433px] text-sm leading-[1.5] text-white/50">
													<Trans>
														Go to{' '}
														<a
															href={DONOR_APP_URL}
															target="_blank"
															rel="noopener noreferrer"
															className="text-olive underline"
														>
															app.worldcoo.com
														</a>
														, open the “Add donation” section and register it with the donation code printed on your
														receipt, or by scanning the code directly. The donation is then certified automatically.
													</Trans>
												</p>
											</div>
										)}
										<Suspense fallback={<FormSkeleton />}>
											<ContactForm variant={audience} submit={submit} onSuccess={onSent} />
										</Suspense>
									</>
								)
							)}
						</div>
					</>
				)}
			</section>
		</Expand>
	);
}

/** A prospective NGO wants the onboarding form, a partner may have a question: neither wins, so two tabs. */
function NgoBody({ tab, submit, onSuccess }: { tab: NgoTab; submit: SubmitCta; onSuccess: () => void }) {
	const { t } = useLingui();
	const navigate = useNavigate();

	const tabs: { value: NgoTab; label: string }[] = [
		{ value: 'collaborate', label: t`I want to collaborate` },
		{ value: 'enquire', label: t`I want to ask` },
	];

	// Controlled by the URL, or Radix's state would drift from it on the back button.
	return (
		<Tabs
			value={tab}
			onValueChange={(value) => {
				const picked = tabs.find(({ value: tabValue }) => tabValue === value);
				if (!picked) return;
				if (picked.value === DEFAULT_NGO_TAB) navigate({ to: '/www/contact/$audience', params: { audience: 'ngo' } });
				else navigate({ to: '/www/contact/$audience/$tab', params: { audience: 'ngo', tab: picked.value } });
			}}
			className="gap-0"
		>
			{/* Overridden on the variant that sets them, so tailwind-merge drops the defaults. */}
			<TabsList
				variant="line"
				className="w-full gap-0 border-b border-white/10 p-0 group-data-horizontal/tabs:h-[53px]"
			>
				{tabs.map((tab) => (
					<TabsTrigger
						key={tab.value}
						value={tab.value}
						className="rounded-none text-sm text-white/50 after:bg-white group-data-horizontal/tabs:after:bottom-0 hover:bg-white/5 hover:text-white data-active:bg-transparent data-active:text-white"
					>
						{tab.label}
					</TabsTrigger>
				))}
			</TabsList>

			<TabsContent value="collaborate" className="flex flex-col items-center gap-4 px-6 py-14 text-center">
				<h2 className="leading-[1.4] font-medium">{t`Do you want to start collaborating?`}</h2>
				<p className="max-w-[337px] text-sm leading-[1.5] text-white/50">
					{t`Fill in the form with your entity's basic information to start the sign-up process.`}
				</p>
				<PillLink variant="white" href={NGO_APPLICATION_FORM_URL}>
					{t`Go to the form`}
				</PillLink>
			</TabsContent>

			{/* Under the `TabsList`, which must stay clickable while the form's chunk arrives. No side inset: the
			    hand-off's inset belongs to a box this panel does not draw, so here it read as stray padding. */}
			<TabsContent value="enquire" className="pt-8">
				<Suspense fallback={<FormSkeleton />}>
					<ContactForm variant="ngo" submit={submit} onSuccess={onSuccess} />
				</Suspense>
			</TabsContent>
		</Tabs>
	);
}
